import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  chakra,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import {
  dateFormatter,
  timeFormatter,
} from '../../../utils/functions/common-utils';
import { getVideoTime } from '../../../utils/functions/format-date';
import i18n from '../../../utils/i18n';
import {
  AdBoardSectionType,
  DocumentMarkupMessageContextDto,
  isVideoMessageContext,
  MessageContext,
  SectionDto,
  VideoMessageContextDto,
} from '../types';

import { EditedAtText } from './EditedAtText';
import { parseAndHighlightMentions } from './HighlightMentions';
import { ViewThreadModal } from './ViewThreadModal';

export interface CommonCellProps {
  content?: string;
  editedAt?: string;
  hidden?: boolean;
  context?: MessageContext;
  sectionType?: AdBoardSectionType;
  attachmentName?: string;
  attachmentOpenedAt?: string;
  threadId?: string;
  messageId: string;
  parentMessageContent?: string;
  getCanExpand: () => boolean;
  getIsExpanded: () => boolean;
  toggleExpanded: (expanded?: boolean) => void;
  subRowCount: number;
  participantView: boolean;
  viewThreadModal: boolean;
  selectedActivity?: SectionDto;
  isGeneralDiscussion: boolean;
}

/* eslint-disable react/prop-types */
export const FeedContentCell: React.FC<CommonCellProps> = ({
  context,
  sectionType,
  attachmentName,
  attachmentOpenedAt,
  content,
  editedAt,
  hidden,
  threadId,
  parentMessageContent,
  getCanExpand,
  getIsExpanded,
  toggleExpanded,
  subRowCount,
  messageId,
  participantView,
  viewThreadModal,
  selectedActivity,
  isGeneralDiscussion,
}) => {
  const showVideo =
    isVideoMessageContext(context) && getVideoTime(context.videoTimeStamp);
  const showDocumentMarkup = (context as DocumentMarkupMessageContextDto)
    ?.highlightedText;
  const showAttachment = sectionType === 'attachment';

  return (
    <Box as="span" display="inline-block" gap={1} data-testid="content">
      {showVideo && (
        <VideoContentCell context={context as VideoMessageContextDto} />
      )}
      {showDocumentMarkup && (
        <DocumentMarkupContentCell
          context={context as DocumentMarkupMessageContextDto}
        />
      )}
      {showAttachment && attachmentName && attachmentOpenedAt && (
        <AttachmentContentCell
          attachmentName={attachmentName}
          attachmentOpenedAt={attachmentOpenedAt}
        />
      )}
      <DefaultContentCell
        content={content}
        editedAt={editedAt}
        hidden={hidden}
        getCanExpand={getCanExpand}
        getIsExpanded={getIsExpanded}
        toggleExpanded={toggleExpanded}
        subRowCount={subRowCount}
        threadId={threadId}
        messageId={messageId}
        participantView={participantView}
        viewThreadModal={viewThreadModal}
        parentMessageContent={parentMessageContent}
        selectedActivity={selectedActivity}
        isGeneralDiscussion={isGeneralDiscussion}
      />
    </Box>
  );
};

type VideoContentCellProps = {
  context: VideoMessageContextDto;
};

const VideoContentCell: React.FC<VideoContentCellProps> = ({ context }) => {
  return <Text>{getVideoTime(context.videoTimeStamp)}</Text>;
};

interface AttachmentContentCellProperties {
  attachmentName: string;
  attachmentOpenedAt: string;
}

const AttachmentContentCell: React.FC<AttachmentContentCellProperties> = ({
  attachmentName,
  attachmentOpenedAt,
}) => {
  return (
    <Text>
      {i18n.t('adBoardDetails.engagement.attachmentReadReceiptString', {
        attachmentName,
        openedDate: dateFormatter(attachmentOpenedAt),
        openedTime: timeFormatter(attachmentOpenedAt),
      })}
    </Text>
  );
};

interface DocumentMarkupContentCellProperties {
  context: DocumentMarkupMessageContextDto;
}

const DocumentMarkupContentCell: React.FC<
  DocumentMarkupContentCellProperties
> = ({ context }) => {
  const [expanded, setExpanded] = useState(false);

  const highlightedText = context.highlightedText;

  const isLong = highlightedText.length > 110;
  const displayText = expanded
    ? highlightedText
    : highlightedText.slice(0, 110);

  return (
    <Box
      borderLeft="solid 3px"
      borderColor="gray.200"
      pl="12px"
      textColor="gray.600"
    >
      <Text as="span">
        {displayText}
        {isLong && !expanded && '... '}
        {isLong && (
          <Button
            onClick={() => setExpanded((prev) => !prev)}
            variant="link"
            size="sm"
            color="keyops.blue"
          >
            {expanded ? 'See less' : 'See more'}
          </Button>
        )}
      </Text>
    </Box>
  );
};

export const DefaultContentCell: React.FC<CommonCellProps> = ({
  content,
  editedAt,
  hidden,
  threadId,
  messageId,
  parentMessageContent,
  getCanExpand,
  getIsExpanded,
  toggleExpanded,
  subRowCount,
  viewThreadModal,
  participantView,
  selectedActivity,
  isGeneralDiscussion,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box>
      <ViewThreadModal
        selectedMessageId={messageId}
        isOpen={isOpen}
        onClose={onClose}
        selectedActivity={selectedActivity}
        isGeneralDiscussion={isGeneralDiscussion}
      />
      {participantView && threadId && (
        <Stack direction="row">
          <Text fontSize="12px" fontWeight="400" lineHeight="24px">
            {i18n.t('adBoardDetails.individualParticipant.replyInThread')}
          </Text>
          {/* Ex: "Reply in thread: titleOfTheThread" */}
          <Button
            variant="link"
            size="sm"
            onClick={onOpen}
            colorScheme="blue"
            fontSize="12px"
            fontWeight="400"
            lineHeight="24px"
            maxWidth="500px"
            justifyContent="left"
            isTruncated
          >
            {parseAndHighlightMentions(parentMessageContent, true)}
          </Button>
        </Stack>
      )}
      <Text>
        {parseAndHighlightMentions(content)}{' '}
        {editedAt && <EditedAtText editedAt={editedAt} />}
        <Text fontSize={12} color={'gray.400'} fontWeight={600} mt={1}>
          {hidden && (
            <chakra.span mr={1}>
              {i18n.t('adBoardDetails.engagement.hidden')}
            </chakra.span>
          )}
        </Text>
      </Text>
      {getCanExpand() && !viewThreadModal && (
        // ex: "View 4 replies" button
        <Button
          variant="link"
          size="sm"
          onClick={() => (participantView ? onOpen() : toggleExpanded())}
          rightIcon={
            participantView ? undefined : getIsExpanded() ? (
              <ChevronUpIcon mt="1px" ml="-6px" boxSize={5} />
            ) : (
              <ChevronDownIcon mt="1px" ml="-6px" boxSize={5} />
            )
          }
          colorScheme="blue"
          alignSelf="flex-start"
          paddingTop={2}
          fontSize="12px"
          fontWeight="400"
          lineHeight="24px"
        >
          {getIsExpanded()
            ? 'Hide replies'
            : `View ${subRowCount} repl${subRowCount === 1 ? 'y' : 'ies'}`}
        </Button>
      )}
    </Box>
  );
};
